<script>
import { computed, ref } from 'vue';
import { useStore } from 'vuex';
import AddressForm from '../AddressForm.vue';
import InfoCard from '../InfoCard.vue';
import { useAccount } from '../../composables/useAccount';
import { useMembershipContext } from '../../composables/useMembershipContext';

export default {
  name: 'MembershipBillingInfo',
  components: { AddressForm, InfoCard },
  setup() {
    const store = useStore();
    const { card, name, email } = useAccount();
    const hasOwnership = computed(() => store.getters.hasOwnership);
    const loading = computed(() => store.state.requestInProgress);
    const isEditing = ref(false);

    const toggleEdit = () => { isEditing.value = !isEditing.value; };

    const currentSubscription = computed(() => store.getters.currentSubscriptions?.[0]);
    const { mutate: updateTaxRate, error: mutateError } = useMembershipContext(currentSubscription?.value?.id).updateTaxRate;

    const billingAddress = computed(() => {
      if (card?.value?.billing_details?.address) return card.value.billing_details.address;
      return '';
    });

    const addressLine1 = computed(() => billingAddress.value?.line1 ?? '');
    const addressLine2 = computed(() => billingAddress.value?.line2 ?? '');
    const cityStateZip = computed(() => {
      const a = billingAddress.value;
      if (!a) return '';
      return [a.city, a.state, a.postal_code].filter(Boolean).join(', ');
    });

    async function submitAddress(address) {
      if (card.value) {
        // we do this call first to check if the zipcode/state combo is valid
        await updateTaxRate({
          price: currentSubscription.value?.plan?.amount,
          address,
        });

        if (mutateError.value) {
          isEditing.value = false;
          return;
        }

        await store.dispatch('updateAddress', {
          paymentMethodId: card.value.id,
          updatedDetails: address,
        });

        isEditing.value = false;
      }
    }

    return {
      card,
      name,
      email,
      hasOwnership,
      loading,
      isEditing,
      toggleEdit,
      billingAddress,
      addressLine1,
      addressLine2,
      cityStateZip,
      mutateError,
      submitAddress,
    };
  },
};
</script>

<template>
  <InfoCard title="Billing Information">
    <template v-if="hasOwnership && !isEditing" #action>
      <button
        class="membership-billing-info__edit-btn"
        aria-label="Edit billing information"
        @click="toggleEdit"
      >
        <font-awesome-icon icon="pen" />
      </button>
    </template>

    <div v-if="hasOwnership">
      <div v-if="isEditing && card">
        <AddressForm
          :name="name"
          :address="billingAddress"
          :loading="loading"
          @addressFormSubmitted="submitAddress"
          @addressFormCancelled="toggleEdit"
        />
      </div>
      <div v-else-if="!card">
        <p class="membership-billing-info__restricted">Please update your card.</p>
      </div>
      <div v-else>
        <div class="membership-billing-info__field">
          <span class="membership-billing-info__field-label">Email</span>
          <span class="membership-billing-info__field-value">{{ email }}</span>
        </div>
        <div v-if="card" class="membership-billing-info__field">
          <span class="membership-billing-info__field-label">Address</span>
          <div class="membership-billing-info__field-value membership-billing-info__address">
            <span>{{ name }}</span>
            <span>{{ addressLine1 }}</span>
            <span v-if="addressLine2">{{ addressLine2 }}</span>
            <span>{{ cityStateZip }}</span>
          </div>
        </div>
      </div>
      <p v-if="mutateError" class="membership-billing-info__error" v-html="mutateError" />
    </div>
    <p v-else class="membership-billing-info__restricted">
      Only the account owner can make billing changes.
    </p>
  </InfoCard>
</template>

<style lang="scss" scoped>
.membership-billing-info {
  &__edit-btn {
    background: none;
    border: none;
    padding: 0.25rem;
    cursor: pointer;
    color: $color-red;
    font-size: 0.85rem;

    &:hover {
      opacity: 0.75;
    }
  }

  &__field {
    display: flex;
    gap: 1rem;
    margin-bottom: 1rem;
    font-size: 0.875rem;

    &:last-child {
      margin-bottom: 0;
    }
  }

  &__field-label {
    min-width: 70px;
    font-size: 0.7rem;
    font-family: $font-stack-heading;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: $fc-greyscale-600;
    padding-top: 0.1rem;
  }

  &__field-value {
    color: $color-black;
    word-break: break-word;
  }

  &__address {
    display: flex;
    flex-direction: column;
    gap: 0.15rem;
  }

  &__error {
    color: $color-red;
    font-size: 0.8rem;
    margin-top: 0.75rem;
  }

  &__restricted {
    font-size: 0.875rem;
    color: $fc-greyscale-700;
  }
}
</style>
